import { useCallback, useState, type JSX } from 'react'
import { Image } from 'primereact/image'
import type { MessageAttachment } from './types.js'
import AttachmentViewer from '../../components/conversations/detail/AttachmentViewer.js'
import { useSignedAttachmentUrl } from '../../hooks/useSignedAttachmentUrl.js'

export interface MediaPreviewProps {
  attachment: MessageAttachment
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function downloadBlob(attachment: MessageAttachment): Promise<void> {
  const res = await fetch(`/api/attachments/${attachment.id}?download=1`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`Download failed (${res.status})`)

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = attachment.filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Renders an attachment inline by MIME family and always exposes
 * View/Download actions. §0.G inbound MIME tolerance: any `image/*` /
 * `video/*` / `audio/*` renders best-effort by prefix match; everything
 * else falls back to a document chip with View/Download links.
 *
 * The agent route `/api/attachments/:id` requires JWT auth, so raw `<a>`/
 * `<img>`/`<video>` `src` values 403 in a new tab or for inline loads. We
 * fetch a JWT-authenticated blob URL via `useSignedAttachmentUrl` and use
 * it for inline media, the inline AttachmentViewer, and downloads.
 */
export default function MediaPreview(props: MediaPreviewProps): JSX.Element {
  const { attachment } = props
  const { url: signedUrl } = useSignedAttachmentUrl(attachment.id)
  const [viewerOpen, setViewerOpen] = useState(false)

  const onDownload = useCallback(() => {
    if (signedUrl) {
      const a = document.createElement('a')
      a.href = signedUrl
      a.download = attachment.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      return
    }
    void (async () => {
      try {
        await downloadBlob(attachment)
      } catch (err) {
        console.error('Download attachment failed:', err)
      }
    })()
  }, [signedUrl, attachment])

  const attachmentWithUrl = signedUrl
    ? { ...attachment, url: signedUrl }
    : attachment

  const actions = (
    <div className="mt-1 flex items-center gap-3 text-xs">
      <button
        type="button"
        onClick={() => setViewerOpen(true)}
        className="text-blue-600 hover:underline"
      >
        View
      </button>
      <button
        type="button"
        onClick={onDownload}
        className="text-blue-600 hover:underline"
      >
        Download
      </button>
    </div>
  )

  const mime = attachment.mimeType

  if (mime.startsWith('image/')) {
    return (
      <div>
        <Image
          src={signedUrl ?? undefined}
          alt={attachment.filename}
          preview
          imageClassName="max-w-xs rounded"
        />
        {actions}
        <AttachmentViewer
          attachments={[attachmentWithUrl]}
          index={0}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onNavigate={() => {}}
        />
      </div>
    )
  }

  if (mime.startsWith('video/')) {
    return (
      <div>
        <video
          data-testid="media-video"
          src={signedUrl ?? undefined}
          controls
          className="max-w-xs rounded"
        />
        {actions}
        <AttachmentViewer
          attachments={[attachmentWithUrl]}
          index={0}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onNavigate={() => {}}
        />
      </div>
    )
  }

  if (mime.startsWith('audio/')) {
    return (
      <div>
        <audio data-testid="media-audio" src={signedUrl ?? undefined} controls />
        {actions}
        <AttachmentViewer
          attachments={[attachmentWithUrl]}
          index={0}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onNavigate={() => {}}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-xs">
      <i className="pi pi-file" aria-hidden />
      <div>
        <div className="font-medium">{attachment.filename}</div>
        <div className="text-gray-500">{formatSize(attachment.sizeBytes)}</div>
        {actions}
        <AttachmentViewer
          attachments={[attachmentWithUrl]}
          index={0}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onNavigate={() => {}}
        />
      </div>
    </div>
  )
}
